import React from 'react';
import { styled } from '@mui/system';
import { helpers } from "@simplybenefits/sb-utils";
import SpecialityProducts from "../Sections/SpecialityProducts.js";
import Text from '../../../../components/Text.js';
import { colors } from '../../../../theme/index.js';
import statusMap from '../../../../services/statusMap.js';
import Tooltip from '../../../../components/Tooltip.js';
import { HeaderBar } from '../../../../components/HeaderBar.js';
import { DividerLine } from '../../../../components/DividerLine.js';
import i18n from '../../../../localization/i18n.js';
import tokens from '../../../../localization/tokens.js';
import * as localizationMaps from '../../../../localization/maps.js';

/**
 * Capitalizes the first letter of a word.
 *
 * @param {string} word - The word to be capitalized.
 * @returns {string} - The word with the first letter capitalized.
 */
function capitalizeFirstLetter(word) {
  if (!word) {
    return null;
  }
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * 
 * @returns 
 */
const benefits = () => ({
  'life': {
    title: i18n.t(tokens.titles.lifeInsurance),
    volumeType: 'number'
  },
  'add': {
    title: i18n.t(tokens.titles["ad&d"]),
    volumeType: 'number'
  },
  'ltd': {
    title: i18n.t(tokens.titles.ltd),
    volumeType: 'number'
  },
  'std': {
    title: i18n.t(tokens.titles.std),
    volumeType: 'number'
  },
  'depLife': {
    title: i18n.t(tokens.titles.depLife),
    volumeType: 'text'
  },
  'ci': {
    title: i18n.t(tokens.titles.ci),
    volumeType: 'text'
  },
  'ciSpouse': {
    title: i18n.t(tokens.titles.ciSpouse),
    volumeType: 'text'
  },
  'ciChild': {
    title: i18n.t(tokens.titles.ciChild),
    volumeType: 'text'
  },
  'health': {
    title: i18n.t(tokens.titles.health),
    volumeType: 'text'
  },
  'outOfCountry': {
    title: i18n.t(tokens.titles.outOfCountry),
    volumeType: 'text'
  },
  'stopLoss': {
    title: i18n.t(tokens.titles.stopLoss),
    volumeType: 'text'
  },
  'hsa': {
    title: i18n.t(tokens.titles.hsa),
    volumeType: 'text'
  },
  'lsa': {
    title: i18n.t(tokens.titles.lsa),
    volumeType: 'text'
  },
  'costPlus': {
    title: i18n.t(tokens.titles.costPlus),
    volumeType: 'text'
  },
  'virtualHealthcare': {
    title: i18n.t(tokens.titles.virtualHealthCare),
    volumeType: 'text'
  },
  'financialAwareness': {
    title: i18n.t(tokens.titles.financialAwareness),
    volumeType: 'text'
  },
  'efap': {
    title: i18n.t(tokens.titles.efap),
    volumeType: 'text'
  },
  'dental': {
    title: i18n.t(tokens.titles.dental),
    volumeType: 'text'
  },
  'optionalBenefits': {
    title: i18n.t(tokens.titles.optional),
    volumeType: 'dash'
  },
  'voluntaryBenefits': {
    title: i18n.t(tokens.titles.voluntary),
    volumeType: 'dash'
  },
  'miscPremiums': {
    title: i18n.t(tokens.titles.misc),
    volumeType: 'dash'
  },
  'specialityProducts': {
    title: i18n.t(tokens.labels.invoices.specialityProducts),
    volumeType: 'dash'
  }
});
const Block = styled('div')({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  paddingRight: 10
});
const RowCondensed = styled('div')({
  display: 'flex',
  flex: 1,
  flexDirection: 'row',
  padding: "3px 8px",
  justifyContent: 'center'
});
const HeaderBarNoFill = styled("div")(({
  theme
}) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center',
  marginTop: 10,
  marginBottom: 10,
  padding: 5,
  borderTop: `1.5px solid ${theme.palette.primary.main}`,
  borderBottom: `1.5px solid ${theme.palette.primary.main}`
}));
const memberMetaBlock = (transaction, classDescription, divisionDescription) => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HeaderBar, null, /*#__PURE__*/React.createElement(Text, {
  medium: true,
  bold: true,
  color: colors.surface,
  customStyle: {
    flex: 1
  }
}, `${transaction.meta.lastname}, ${transaction.meta.firstname}`), /*#__PURE__*/React.createElement(Text, {
  medium: true,
  bold: true,
  color: colors.surface
}, `${i18n.t(tokens.labels.invoices.certificate)}: ${transaction.meta.cert}`)), /*#__PURE__*/React.createElement(RowCondensed, null, /*#__PURE__*/React.createElement(Text, {
  small: true,
  bold: true,
  customStyle: {
    flex: 1
  }
}, i18n.t(tokens.labels.claims.status)), /*#__PURE__*/React.createElement(Text, {
  small: true,
  bold: true,
  customStyle: {
    flex: 1
  }
}, i18n.t(tokens.labels.invoices.class)), /*#__PURE__*/React.createElement(Text, {
  small: true,
  bold: true,
  customStyle: {
    flex: 1
  }
}, i18n.t(tokens.labels.invoices.division)), /*#__PURE__*/React.createElement(Text, {
  small: true,
  bold: true,
  customStyle: {
    flex: 1
  }
}, i18n.t(tokens.labels.province)), transaction.meta.payrollID ? /*#__PURE__*/React.createElement(Text, {
  small: true,
  bold: true,
  customStyle: {
    flex: 1
  }
}, i18n.t(tokens.labels.invoices.payrollID)) : null, /*#__PURE__*/React.createElement(Text, {
  small: true,
  bold: true,
  customStyle: {
    flex: 2
  }
}, i18n.t(tokens.labels.invoices.notes))), /*#__PURE__*/React.createElement(RowCondensed, null, /*#__PURE__*/React.createElement(Text, {
  small: true,
  customStyle: {
    flex: 1
  }
}, localizationMaps.fromEnglish("memberStatuses", statusMap[transaction.meta.status])), classDescription ? /*#__PURE__*/React.createElement(Tooltip, {
  tip: classDescription,
  placement: "top-start",
  customStyle: {
    flex: 1
  }
}, /*#__PURE__*/React.createElement(Text, {
  small: true
}, transaction.meta.className)) : /*#__PURE__*/React.createElement(Text, {
  small: true,
  customStyle: {
    flex: 1
  }
}, transaction.meta.className), divisionDescription ? /*#__PURE__*/React.createElement(Tooltip, {
  tip: divisionDescription,
  placement: "top-start",
  customStyle: {
    flex: 1
  }
}, /*#__PURE__*/React.createElement(Text, {
  small: true,
  customStyle: {
    flex: 1
  }
}, transaction.meta.divisionName)) : /*#__PURE__*/React.createElement(Text, {
  small: true,
  customStyle: {
    flex: 1
  }
}, transaction.meta.divisionName), /*#__PURE__*/React.createElement(Text, {
  small: true,
  customStyle: {
    flex: 1
  }
}, localizationMaps.fromEnglish("provinces", transaction.meta.province)), transaction.meta.payrollID ? /*#__PURE__*/React.createElement(Text, {
  small: true,
  customStyle: {
    flex: 1
  }
}, transaction.meta.payrollID) : null, /*#__PURE__*/React.createElement(Text, {
  small: true,
  customStyle: {
    flex: 2
  }
}, transaction.notes.length > 0 ? transaction.notes : i18n.t(tokens.labels.invoices.none))));

/**
 * @param isMemberInvoice
 * @param transaction
 * @param classDescription
 * @param divisionDescription
 * @param testid
 * @param style
 * @param isPrivateLabel
 * @returns {JSX.Element}
 * @constructor
 */
const MemberPremiumBlock = ({
  isMemberInvoice,
  transaction,
  classDescription,
  divisionDescription,
  testid,
  style,
  isPrivateLabel
}) => {
  if (transaction.marketplaceOnly) {
    return /*#__PURE__*/React.createElement(Block, {
      style: style
    }, memberMetaBlock(transaction, classDescription, divisionDescription), /*#__PURE__*/React.createElement(HeaderBarNoFill, null, /*#__PURE__*/React.createElement(Text, {
      small: true,
      bold: true,
      customStyle: {
        flex: 2,
        textAlign: 'center'
      }
    }, i18n.t(tokens.labels.marketplace.marketplaceProduct)), /*#__PURE__*/React.createElement(Text, {
      small: true,
      bold: true,
      customStyle: {
        flex: 1,
        textAlign: 'center'
      }
    }, i18n.t(tokens.labels.invoices.premium)), /*#__PURE__*/React.createElement(Text, {
      small: true,
      bold: true,
      customStyle: {
        flex: 1,
        textAlign: 'center'
      }
    }, i18n.t(tokens.labels.invoices.taxes)), /*#__PURE__*/React.createElement(Text, {
      small: true,
      bold: true,
      customStyle: {
        flex: 1,
        textAlign: 'center'
      }
    }, i18n.t(tokens.labels.invoices.adjustments)), /*#__PURE__*/React.createElement(Text, {
      small: true,
      bold: true,
      customStyle: {
        flex: 1,
        textAlign: 'center'
      }
    }, i18n.t(tokens.labels.total))), transaction.amounts.voluntaryBenefits.records.map((record, index) => {
      const name = `${record.name} with ${record.carrier}`;
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: index
      }, /*#__PURE__*/React.createElement(RowCondensed, null, /*#__PURE__*/React.createElement(Text, {
        small: true,
        customStyle: {
          flex: 2,
          textAlign: 'center'
        }
      }, name), /*#__PURE__*/React.createElement(Text, {
        small: true,
        customStyle: {
          flex: 1,
          textAlign: 'center'
        }
      }, helpers.formatNumber(record.premium, 2, true)), /*#__PURE__*/React.createElement(Text, {
        small: true,
        customStyle: {
          flex: 1,
          textAlign: 'center'
        }
      }, helpers.formatNumber(record.taxes || 0, 2, true)), /*#__PURE__*/React.createElement(Text, {
        small: true,
        customStyle: {
          flex: 1,
          textAlign: 'center'
        }
      }, "-"), /*#__PURE__*/React.createElement(Text, {
        small: true,
        customStyle: {
          flex: 1,
          textAlign: 'center'
        }
      }, helpers.formatNumber(record.premium + (record.taxes || 0), 2, true))));
    }), /*#__PURE__*/React.createElement(DividerLine, null), /*#__PURE__*/React.createElement(RowCondensed, null, /*#__PURE__*/React.createElement(Text, {
      small: true,
      bold: true,
      customStyle: {
        flex: 2,
        textAlign: 'center'
      }
    }, i18n.t(tokens.labels.total)), /*#__PURE__*/React.createElement(Text, {
      small: true,
      bold: true,
      customStyle: {
        flex: 1,
        textAlign: 'center'
      }
    }, helpers.formatNumber(transaction.amounts.voluntaryBenefits.premium, 2, true)), /*#__PURE__*/React.createElement(Text, {
      small: true,
      bold: true,
      customStyle: {
        flex: 1,
        textAlign: 'center'
      }
    }, helpers.formatNumber(transaction.amounts.voluntaryBenefits.GST + transaction.amounts.voluntaryBenefits.PST + transaction.amounts.voluntaryBenefits.RST + transaction.amounts.voluntaryBenefits.HST, 2, true)), /*#__PURE__*/React.createElement(Text, {
      small: true,
      bold: true,
      customStyle: {
        flex: 1,
        textAlign: 'center'
      }
    }, helpers.formatNumber(transaction.amounts.voluntaryBenefits.adjustments.net, 2, true)), /*#__PURE__*/React.createElement(Text, {
      small: true,
      bold: true,
      customStyle: {
        flex: 1,
        textAlign: 'center'
      }
    }, helpers.formatNumber(transaction.amounts.totals.voluntaryBenefitsGrandTotal, 2, true))), /*#__PURE__*/React.createElement("span", {
      id: testid
    }));
  }
  if (isPrivateLabel) {
    return /*#__PURE__*/React.createElement(Block, {
      style: style
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        flexDirection: "row"
      }
    }, transaction.meta.parentInvoiceNum ? /*#__PURE__*/React.createElement("div", {
      style: {
        marginRight: 25
      }
    }, /*#__PURE__*/React.createElement(Text, {
      small: true,
      bold: true,
      customStyle: {
        marginTop: 4,
        marginBottom: 8
      }
    }, i18n.t(tokens.labels.invoices.parentInvoiceNumber)), /*#__PURE__*/React.createElement(Text, {
      small: true
    }, `${transaction.meta.parentInvoiceNum}`)) : null, transaction.meta.invoiceNum ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
      small: true,
      bold: true,
      customStyle: {
        marginTop: 4,
        marginBottom: 8
      }
    }, i18n.t(tokens.labels.invoices.invoiceNumber)), /*#__PURE__*/React.createElement(Text, {
      small: true
    }, `${transaction.meta.invoiceNum}`)) : null), memberMetaBlock(transaction, classDescription, divisionDescription), /*#__PURE__*/React.createElement(SpecialityProducts, {
      specialityProducts: transaction.amounts.specialityProducts,
      isMemberBlock: true
    }));
  }
  return /*#__PURE__*/React.createElement(Block, {
    style: style
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      flexDirection: "row"
    }
  }, transaction.meta.parentInvoiceNum ? /*#__PURE__*/React.createElement("div", {
    style: {
      marginRight: 25
    }
  }, /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      marginTop: 4,
      marginBottom: 8
    }
  }, i18n.t(tokens.labels.invoices.parentInvoiceNumber)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, `${transaction.meta.parentInvoiceNum}`)) : null, transaction.meta.invoiceNum ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      marginTop: 4,
      marginBottom: 8
    }
  }, i18n.t(tokens.labels.invoices.invoiceNumber)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, `${transaction.meta.invoiceNum}`)) : null), memberMetaBlock(transaction, classDescription, divisionDescription), /*#__PURE__*/React.createElement(HeaderBarNoFill, null, /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, i18n.t(tokens.labels.coverage.benefit)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, i18n.t(tokens.labels.invoices.volume)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, i18n.t(tokens.labels.invoices.premium)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, i18n.t(tokens.labels.invoices.adj)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, i18n.t(tokens.labels.invoices.gstHst)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, i18n.t(tokens.labels.invoices.pst)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, i18n.t(tokens.labels.total))), Object.keys(benefits()).map((benefit, index) => {
    const benefitParams = benefits()[benefit];
    let premium = 0;
    let adj = 0;
    let HST = 0;
    let GST = 0;
    let PST = 0;
    let RST = 0;
    let volume = '-';

    // if the benefit exists in the invoice
    if (transaction.amounts[benefit]) {
      premium = transaction.amounts[benefit].premium;
      HST = transaction.amounts[benefit].HST + transaction.amounts[benefit].adjustments.HST;
      GST = transaction.amounts[benefit].GST + transaction.amounts[benefit].adjustments.GST;
      PST = transaction.amounts[benefit].PST + transaction.amounts[benefit].adjustments.PST;
      RST = (transaction.amounts[benefit].RST || 0) + (transaction.amounts[benefit].adjustments.RST || 0);
      adj = transaction.amounts[benefit].adjustments.net;
      if (benefitParams.volumeType === 'number') {
        volume = helpers.formatNumber(Math.ceil(transaction.amounts[benefit].volume), 0, false);
      } else if (benefitParams.volumeType === 'text') {
        volume = localizationMaps.fromEnglish("coverageTypes", capitalizeFirstLetter(transaction.amounts[benefit].coverageType));
      }
    }
    if (premium || adj || ['hsa', 'lsa', 'miscPremiums', 'voluntaryBenefits'].includes(benefit)) {
      // we don't show the voluntary benefits amounts on the account invoices
      if (benefit === 'voluntaryBenefits' && !isMemberInvoice) {
        return null;
      }
      return /*#__PURE__*/React.createElement(RowCondensed, {
        key: index
      }, /*#__PURE__*/React.createElement(Text, {
        small: true,
        customStyle: {
          flex: 1,
          textAlign: 'center'
        }
      }, benefitParams.title), /*#__PURE__*/React.createElement(Text, {
        small: true,
        customStyle: {
          flex: 1,
          textAlign: 'center'
        }
      }, volume), /*#__PURE__*/React.createElement(Text, {
        small: true,
        customStyle: {
          flex: 1,
          textAlign: 'center'
        }
      }, helpers.formatNumber(premium, 2, true)), /*#__PURE__*/React.createElement(Text, {
        small: true,
        customStyle: {
          flex: 1,
          textAlign: 'center'
        }
      }, helpers.formatNumber(adj, 2, true)), /*#__PURE__*/React.createElement(Text, {
        small: true,
        customStyle: {
          flex: 1,
          textAlign: 'center'
        }
      }, helpers.formatNumber(GST + HST, 2, true)), /*#__PURE__*/React.createElement(Text, {
        small: true,
        customStyle: {
          flex: 1,
          textAlign: 'center'
        }
      }, helpers.formatNumber(PST + RST, 2, true)), /*#__PURE__*/React.createElement(Text, {
        bold: true,
        small: true,
        customStyle: {
          flex: 1,
          textAlign: 'center'
        }
      }, helpers.formatNumber(premium + GST + HST + PST + RST + adj, 2, true)));
    }
    return null;
  }), /*#__PURE__*/React.createElement(DividerLine, null), /*#__PURE__*/React.createElement(React.Fragment, null, !isMemberInvoice && transaction.amounts.totals.CCFee > 0 && /*#__PURE__*/React.createElement(RowCondensed, null, /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, i18n.t(tokens.labels.invoices.convenienceFee)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "-"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "-"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "-"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "-"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "-"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, helpers.formatNumber(transaction.amounts.totals.CCFee, 2, true))), isMemberInvoice && (transaction.amounts.totals.voluntaryBenefitsCCFee > 0 || transaction.amounts.totals.CCFee > 0) && /*#__PURE__*/React.createElement(RowCondensed, null, /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, i18n.t(tokens.labels.invoices.convenienceFee)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "-"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "-"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "-"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "-"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "-"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, helpers.formatNumber(transaction.amounts.totals.voluntaryBenefitsCCFee + transaction.amounts.totals.CCFee, 2, true))), (transaction.amounts.totals.carryForward > 0 || transaction.amounts.totals.carryForward < 0) && isMemberInvoice && /*#__PURE__*/React.createElement(RowCondensed, null, /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, i18n.t(tokens.labels.invoices.carryForward)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "-"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "-"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "-"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "-"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "-"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, helpers.formatNumber(transaction.amounts.totals.carryForward, 2, true))), /*#__PURE__*/React.createElement(RowCondensed, null, /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "Total"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "-"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, helpers.formatNumber(transaction.amounts.totals.premium, 2, true)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, helpers.formatNumber(transaction.amounts.totals.adjustments.net, 2, true)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, helpers.formatNumber(transaction.amounts.totals.HST + transaction.amounts.totals.GST, 2, true)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, helpers.formatNumber(transaction.amounts.totals.PST + (transaction.amounts.totals.RST || 0), 2, true)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, helpers.formatNumber(transaction.amounts.totals.grandTotal + (transaction.amounts.totals.carryForward || 0), 2, true)))), transaction.amounts.optionalBenefits.records && transaction.amounts.optionalBenefits.records.length > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HeaderBarNoFill, null, /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1.5,
      textAlign: 'center'
    }
  }, i18n.t(tokens.titles.optionalBenefits)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "Volume"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "Premium")), transaction.amounts.optionalBenefits.records.map((record, index) => {
    const name = `${record.name} with ${record.carrier}`;
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: index
    }, /*#__PURE__*/React.createElement(RowCondensed, null, /*#__PURE__*/React.createElement(Text, {
      small: true,
      customStyle: {
        flex: 1.5,
        textAlign: 'center'
      }
    }, name), /*#__PURE__*/React.createElement(Text, {
      small: true,
      customStyle: {
        flex: 1,
        textAlign: 'center'
      }
    }, helpers.formatNumber(Math.ceil(record.volume), 0, false)), /*#__PURE__*/React.createElement(Text, {
      small: true,
      customStyle: {
        flex: 1,
        textAlign: 'center'
      }
    }, helpers.formatNumber(record.premium, 2, true))));
  })) : null, isMemberInvoice && transaction.amounts.voluntaryBenefits.records && transaction.amounts.voluntaryBenefits.records.length > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HeaderBarNoFill, null, /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1.5,
      textAlign: 'center'
    }
  }, i18n.t(tokens.titles.voluntaryBenefits)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "Volume"), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, "Premium")), transaction.amounts.voluntaryBenefits.records.map((record, index) => {
    const name = `${record.name} with ${record.carrier}`;
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: index
    }, /*#__PURE__*/React.createElement(RowCondensed, null, /*#__PURE__*/React.createElement(Text, {
      small: true,
      customStyle: {
        flex: 1.5,
        textAlign: 'center'
      }
    }, name), /*#__PURE__*/React.createElement(Text, {
      small: true,
      customStyle: {
        flex: 1,
        textAlign: 'center'
      }
    }, helpers.formatNumber(Math.ceil(record.volume), 0, false)), /*#__PURE__*/React.createElement(Text, {
      small: true,
      customStyle: {
        flex: 1,
        textAlign: 'center'
      }
    }, helpers.formatNumber(record.premium, 2, true))));
  })) : null, transaction.amounts.miscPremiums.records.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HeaderBarNoFill, null, /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1.5,
      textAlign: 'center'
    }
  }, i18n.t(tokens.labels.invoices.miscPremiums)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, i18n.t(tokens.labels.invoices.volume)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, i18n.t(tokens.labels.invoices.premium))), transaction.amounts.miscPremiums.records.map((record, index) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: index
  }, /*#__PURE__*/React.createElement(RowCondensed, null, /*#__PURE__*/React.createElement(Text, {
    small: true,
    customStyle: {
      flex: 1.5,
      textAlign: 'center'
    }
  }, record.type), /*#__PURE__*/React.createElement(Text, {
    small: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, helpers.formatNumber(Math.ceil(record.volume), 0, false)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, helpers.formatNumber(record.premium, 2, true)))))), transaction.amounts.specialityProducts && Object.values(transaction.amounts.specialityProducts.products).length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HeaderBarNoFill, null, /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1.5,
      textAlign: 'center'
    }
  }, i18n.t(tokens.labels.invoices.specialityProducts)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, i18n.t(tokens.labels.invoices.volume)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    bold: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, i18n.t(tokens.labels.invoices.premium))), Object.values(transaction.amounts.specialityProducts.products).map((record, index) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: index
  }, /*#__PURE__*/React.createElement(RowCondensed, null, /*#__PURE__*/React.createElement(Text, {
    small: true,
    customStyle: {
      flex: 1.5,
      textAlign: 'center'
    }
  }, `${record.name} (${record.carrier})`), /*#__PURE__*/React.createElement(Text, {
    small: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, helpers.formatNumber(Math.ceil(record.volume), 0, false)), /*#__PURE__*/React.createElement(Text, {
    small: true,
    customStyle: {
      flex: 1,
      textAlign: 'center'
    }
  }, helpers.formatNumber(record.premium, 2, true)))))), /*#__PURE__*/React.createElement("span", {
    id: testid
  }));
};
export default MemberPremiumBlock;